import { trans } from '@/mixins/i18n';
import { DateTime } from 'luxon';

export type WindowUnit = 'minutes' | 'hours' | 'days' | 'weeks' | 'months';

export type WindowDuration = { amount: number; unit: WindowUnit };

export const MIN_WINDOW_MINUTES = 15;

// Largest exactly-fitting unit wins. Luxon's single-unit diff is calendar
// aware, so a one-day window spanning a DST shift still decomposes to
// "1 day" rather than 23 or 25 hours.
export function decomposeWindow(start: DateTime | null | undefined, end: DateTime | null | undefined): WindowDuration | null {
    if (!start?.isValid || !end?.isValid || end <= start) {
        return null;
    }

    // Months are calendar-length, so an integer month diff isn't guaranteed to
    // round-trip (Jan 31 + 1 month clamps to Feb 28). Only accept months when
    // adding them back reproduces the exact end; otherwise fall through to a
    // fixed-length unit so the decompose/add round-trip stays lossless.
    const months = end.diff(start, 'months').months;
    if (Number.isInteger(months) && months >= 1 && start.plus({ months }).toMillis() === end.toMillis()) {
        return { amount: months, unit: 'months' };
    }

    for (const unit of ['weeks', 'days', 'hours'] as const) {
        const amount = end.diff(start, unit).get(unit);
        if (Number.isInteger(amount) && amount >= 1) {
            return { amount, unit };
        }
    }

    // The wire format has minute granularity, so minutes are always exact —
    // this fallback makes decompose/add round-trips lossless.
    return { amount: Math.round(end.diff(start, 'minutes').minutes), unit: 'minutes' };
}

export function addWindow(start: DateTime, duration: WindowDuration): DateTime {
    return start.plus({ [duration.unit]: duration.amount });
}

// The backend rejects windows shorter than 15 minutes.
export function clampWindowAmount(duration: WindowDuration): WindowDuration {
    const minimum = duration.unit === 'minutes' ? MIN_WINDOW_MINUTES : 1;
    const amount = Number.isFinite(duration.amount) ? Math.max(minimum, Math.floor(duration.amount)) : minimum;

    return { ...duration, amount };
}

export function windowDurationLabel(duration: WindowDuration): string {
    return `${duration.amount} ${trans(`general.unit.${duration.unit}`, { '%d': duration.amount })}`;
}
